import { useState, useEffect } from "react";
import { useParams } from "react-router";

import {
  Container,
  Grid,
  Typography,
  CircularProgress,
  Box,
  useTheme
} from "@mui/material";

import { DEFAULT_START_DATE } from "@aclymatepackages/constants";
import { nameToFirstName } from "@aclymatepackages/converters";
import {
  DefaultPaper,
  LoadingButton,
  TextField
} from "@aclymatepackages/atoms";

import { useNewEmployeeForm } from "../../hooks/employees";

import MultiPartForm from "../flows/MultipartForm";

import { fetchOurApi } from "../../../helpers/utils/apiCalls";
import {
  SurveyLanguageProvider,
  useSurveyLanguage
} from "../../../helpers/contexts/surveyLanguage";
import SurveyLanguageToggle from "../../atoms/SurveyLanguageToggle";

const Layout = ({ children }) => {
  const theme = useTheme();

  return (
    <Box
      style={{
        backgroundImage:
          "url('/images/platform/employee-survey-background.jpg')",
        backgroundSize: "cover"
      }}
    >
      <Box
        style={{
          position: "fixed",
          top: theme.spacing(2),
          left: theme.spacing(2),
          zIndex: 1400
        }}
      >
        <SurveyLanguageToggle />
      </Box>
      <Grid
        container
        direction="column"
        style={{
          height: "100vh",
          boxSizing: "border-box",
          overflow: "hidden"
        }}
        wrap="nowrap"
      >
        <Grid
          item
          style={{
            flexGrow: 1,
            overflowY: "auto",
            position: "relative"
          }}
        >
          <Grid
            container
            direction="column"
            justifyContent="center"
            style={{ minHeight: "100%", padding: theme.spacing(2) }}
          >
            {children}
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
};

const SurveyAuthentication = ({
  passwordPin,
  setPasswordPin,
  checkEmailAndPasswordLoading,
  loginError,
  checkEmailAndPin,
  companyName
}) => {
  const { translate } = useSurveyLanguage();

  return (
    <Container maxWidth="sm">
      <DefaultPaper>
        <Grid
          container
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={2}
        >
          <Grid item style={{ marginBottom: "50px", paddingTop: "20px" }}>
            <img
              src="/images/aclymate-full-logo.png"
              alt="RWT logo"
              width={250}
            />
          </Grid>
          <Grid item>
            <Typography variant="h5" align="center">
              {translate("auth.companyEmployeeSurvey", { companyName })}
            </Typography>
          </Grid>
          <Grid item container xs={12}>
            <TextField
              label={translate("auth.enterPasswordPin")}
              value={passwordPin}
              setValue={(value) => setPasswordPin(value)}
            />
          </Grid>
          {loginError && (
            <Grid item>
              <Typography align="center" variant="subtitle2" color="error">
                {loginError}
              </Typography>
            </Grid>
          )}
          <Grid item>
            <LoadingButton
              label={translate("auth.login")}
              variant="contained"
              color="primary"
              disabled={!passwordPin}
              isLoading={checkEmailAndPasswordLoading}
              onClick={() => checkEmailAndPin()}
            />
          </Grid>
        </Grid>
      </DefaultPaper>
    </Container>
  );
};

const NewEmployeeSurvey = ({
  companyName,
  companyStartDate,
  employeeData,
  mostRecentAccountingDate
}) => {
  const { companyId } = useParams();
  const { translate } = useSurveyLanguage();

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [passwordPin, setPasswordPin] = useState("");
  const [checkEmailAndPasswordLoading, setCheckEmailAndPasswordLoading] =
    useState(false);
  const [loginError, setLoginError] = useState("");

  const surveyThankYouStep = [
    {
      label: translate("thankYou.label"),
      title: translate("thankYou.title"),
      subtitle: translate("thankYou.subtitle"),
      titleAlign: "center",
      noButton: true
    }
  ];

  const newEmployeeForm = useNewEmployeeForm({
    existingEmployee: employeeData,
    surveyThankYouStep,
    companyName,
    companyStartDate,
    mostRecentAccountingDate
  });

  const { name = "" } = employeeData || {};
  const firstName = employeeData ? nameToFirstName(name) : "";
  const welcomeRow = {
    label: translate("welcome.title", { firstName }),
    title: translate("welcome.title", { firstName }),
    titleAlign: "center",
    input: (
      <Typography variant="subtitle1">
        {translate("welcome.description", { companyName })}
      </Typography>
    ),
    buttonText: translate("welcome.getStarted")
  };

  const completeNewEmployeeForm = [welcomeRow, ...newEmployeeForm];

  const checkEmailAndPin = async () => {
    setCheckEmailAndPasswordLoading(true);
    await fetchOurApi({
      path: `/individuals/check-valid-user`,
      method: "POST",
      data: {
        companyId,
        passwordPin
      },
      callback: ({ success, message }) => {
        if (message) {
          setLoginError(message);
        }
        return setIsAuthenticated(success);
      }
    }).catch((e) => console.log(e));
    return setCheckEmailAndPasswordLoading(false);
  };

  return (
    <>
      {isAuthenticated || !!employeeData ? (
        <MultiPartForm
          type="employeeSurvey"
          forms={completeNewEmployeeForm}
          submitLoadingText={translate("common.pleaseWait")}
        />
      ) : (
        <SurveyAuthentication
          passwordPin={passwordPin}
          setPasswordPin={setPasswordPin}
          checkEmailAndPasswordLoading={checkEmailAndPasswordLoading}
          loginError={loginError}
          checkEmailAndPin={checkEmailAndPin}
          companyName={companyName}
        />
      )}
    </>
  );
};

const EmployeeSurveyContent = () => {
  const { companyId, employeeId = "", surveyType } = useParams();
  const { translate } = useSurveyLanguage();

  const [dataLoading, setDataLoading] = useState(true);
  const [dbData, setDbData] = useState({});
  const [dataFetched, setDataFetched] = useState(false);

  useEffect(() => {
    const fetchSurveyData = async (path) =>
      await fetchOurApi({
        accountId: companyId,
        path: `/surveys/${path}`,
        method: "GET",
        callback: (res) => res
      });

    const fetchAndSetSurveyData = async () => {
      const [companyData, employeeData] = await Promise.all([
        fetchSurveyData(`company/${companyId}/data`),
        employeeId
          ? fetchSurveyData(`employee/${employeeId}/${companyId}`)
          : Promise.resolve(null)
      ]);

      setDbData({ companyData, employeeData });
      return setDataLoading(false);
    };

    if (companyId && !dataFetched) {
      setDataFetched(true);
      fetchAndSetSurveyData();
    }
  }, [employeeId, companyId, dbData, dataFetched]);

  const { employeeData = {}, companyData = {} } = dbData;
  const { status } = employeeData || {};
  const {
    name: companyName,
    startDate = DEFAULT_START_DATE,
    isEmployeeSurveyAccessible,
    mostRecentAccountingDate
  } = companyData || {};

  const InvalidPage = ({ title, subtitle }) => (
    <Container maxWidth="sm">
      <DefaultPaper>
        <Typography variant="h5" align="center" gutterBottom>
          {title}
        </Typography>
        <Typography variant="body1" align="center">
          {subtitle}
        </Typography>
      </DefaultPaper>
    </Container>
  );

  const displaySurvey = () => {
    if (!companyId || !surveyType) {
      return (
        <InvalidPage
          title={translate("error.occurred")}
          subtitle={translate("error.reloadPage")}
        />
      );
    }

    if (dataLoading) {
      return (
        <Grid container justifyContent="center">
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      );
    }

    if (!isEmployeeSurveyAccessible) {
      return (
        <InvalidPage
          title={translate("error.upgradeSubscription", { companyName })}
          subtitle={translate("error.upgradeSubtitle")}
        />
      );
    }

    if (status === "terminated") {
      return (
        <InvalidPage
          title={translate("error.pageExpired")}
          subtitle={translate("error.pageExpiredSubtitle")}
        />
      );
    }

    return (
      <NewEmployeeSurvey
        companyName={companyName}
        companyStartDate={startDate}
        employeeData={employeeData}
        mostRecentAccountingDate={mostRecentAccountingDate}
      />
    );
  };

  return (
    <Layout>
      <Grid item style={{ maxHeight: "100%" }}>
        {displaySurvey()}
      </Grid>
    </Layout>
  );
};

const EmployeeSurvey = () => (
  <SurveyLanguageProvider>
    <EmployeeSurveyContent />
  </SurveyLanguageProvider>
);
export default EmployeeSurvey;
